/**
 * Helpers for the school's profile photo: the initials badge shown while there is no photo, and
 * the downscale applied before a picked photo is saved.
 */

/** Longest edge of a saved school photo, in pixels. */
export const LOGO_MAX_DIMENSION = 256;

/** Largest file a school may pick, before downscaling. */
export const MAX_LOGO_FILE_SIZE = 5 * 1024 * 1024;

/** First letters of up to three words of the school name, e.g. "Jazz Academy" gives "JA". */
export function buildSchoolInitials(schoolName: string | undefined): string {
    if (!schoolName) return 'SCH';
    const initials = schoolName.trim().split(/\s+/)
        .filter(word => word.length > 0)
        .slice(0, 3)
        .map(word => word.charAt(0).toUpperCase())
        .join('');
    return initials || 'SCH';
}

/**
 * Reads an image file and returns it as a PNG data URL no larger than `maxDimension` on its
 * longest edge. The photo is stored inline on the school profile and travels in an event, so a
 * full-size upload is shrunk here instead.
 */
export function resizeImageFile(file: File, maxDimension: number = LOGO_MAX_DIMENSION): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Image could not be read'));
        reader.onload = () => {
            const source = new Image();
            source.onerror = () => reject(new Error('Image could not be decoded'));
            source.onload = () => {
                const scale = Math.min(1, maxDimension / Math.max(source.width, source.height));
                const width = Math.max(1, Math.round(source.width * scale));
                const height = Math.max(1, Math.round(source.height * scale));
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const context = canvas.getContext('2d');
                if (!context) {
                    // No canvas (very old browser): keep the original rather than losing the photo.
                    resolve(reader.result as string);
                    return;
                }
                context.drawImage(source, 0, 0, width, height);
                resolve(canvas.toDataURL('image/png'));
            };
            source.src = reader.result as string;
        };
        reader.readAsDataURL(file);
    });
}
